import React, { useState } from 'react';
import { CheckCircle2, Link2, XCircle } from 'lucide-react';
import { buildArchiveShareUrl } from '../routing';
import { ARCHIVE_COPY_LINK_VISIBILITY_REMINDER_COPY } from '../archiveRetentionCopy';
import {
  copyTextToClipboard,
  COPY_LINK_FAILURE_MESSAGE,
  COPY_LINK_SUCCESS_MESSAGE,
} from '../copyToClipboard';

interface ArchiveCopyLinkButtonProps {
  leagueId: string;
  archiveId: string;
  className?: string;
  label?: string;
  /** The classic archive page keeps the signed-in reminder. The stream watch page does not. */
  showVisibilityReminder?: boolean;
}

type CopyFeedback = 'success' | 'failure' | null;

const ArchiveCopyLinkButton: React.FC<ArchiveCopyLinkButtonProps> = ({
  leagueId,
  archiveId,
  className = '',
  label = 'Copy archive link',
  showVisibilityReminder = true,
}) => {
  const [feedback, setFeedback] = useState<CopyFeedback>(null);

  const handleCopy = async () => {
    const url = buildArchiveShareUrl(leagueId, archiveId);
    const copied = await copyTextToClipboard(url);
    setFeedback(copied ? 'success' : 'failure');
  };

  const message =
    feedback === 'success'
      ? COPY_LINK_SUCCESS_MESSAGE
      : feedback === 'failure'
        ? COPY_LINK_FAILURE_MESSAGE
        : null;

  return (
    <span className={`inline-flex flex-col items-start gap-1 min-w-0 ${className}`.trim()}>
      <button
        type="button"
        onClick={() => void handleCopy()}
        className="inline-flex min-h-[44px] items-center gap-1.5 text-primary-300 hover:text-primary-200 text-sm"
        data-testid="archive-copy-link-button"
      >
        <Link2 className="h-4 w-4 shrink-0" aria-hidden />
        {label}
      </button>
      {message ? (
        <span
          className={`inline-flex items-start gap-1.5 text-xs ${
            feedback === 'success' ? 'text-emerald-300' : 'text-amber-200'
          }`}
          role="status"
          data-testid="archive-copy-link-message"
        >
          {feedback === 'success' ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
          ) : (
            <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
          )}
          <span>{message}</span>
        </span>
      ) : null}
      {showVisibilityReminder ? (
        <span className="text-xs text-secondary-500" data-testid="archive-copy-link-visibility-reminder">
          {ARCHIVE_COPY_LINK_VISIBILITY_REMINDER_COPY}
        </span>
      ) : null}
    </span>
  );
};

export default ArchiveCopyLinkButton;
